import React, { useState } from 'react';
import { Goal } from '../../types/goals';
import { Button } from '../UI';
import logger from '../../utils/logger';

interface GoalReminderNotificationProps {
  goal: Goal;
  onDismiss?: () => void;
  onGoToGoal?: (goalId: string) => void;
}

const GoalReminderNotification: React.FC<GoalReminderNotificationProps> = ({
  goal,
  onDismiss,
  onGoToGoal
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Handle goal navigation with error handling
  const handleGoToGoal = async () => {
    if (!onGoToGoal) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      onGoToGoal(goal.id);
    } catch (err) {
      logger.error('Error navigating to goal:', err);
      setError('Failed to navigate to goal');
      setIsLoading(false);
    }
  };
  
  // Handle dismissal with error handling
  const handleDismiss = async () => {
    if (!onDismiss) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await onDismiss();
    } catch (err) {
      logger.error('Error dismissing reminder:', err);
      setError('Failed to dismiss');
      setIsLoading(false);
    }
  };

  // Get goal progress text
  const getProgressText = () => {
    try {
      const progress = Math.round(goal.progress || 0);
      return `${progress}% complete`;
    } catch (err) {
      logger.error('Error calculating progress text:', err);
      return 'Progress unavailable';
    }
  };

  // Get goal remaining text based on type
  const getRemainingText = () => {
    try {
      // Handle different goal types
      if (goal.type === 'time' && goal.timeTarget && goal.currentTime !== undefined) {
        const remaining = goal.timeTarget - goal.currentTime;
        const unit = goal.timeUnit || 'minutes';
        return `${remaining} ${unit} remaining`;
      }

      if (goal.targetValue !== undefined && goal.currentValue !== undefined) {
        const remaining = goal.targetValue - goal.currentValue;
        return `${remaining} ${goal.unitShort || ''} remaining`;
      }

      return '';
    } catch (err) {
      logger.error('Error calculating remaining text:', err);
      return '';
    }
  };
  
  // Safely get goal name
  const getGoalName = () => {
    return goal?.name || 'Goal Reminder';
  };
  
  // Safely get goal progress percentage
  const getProgressPercentage = () => {
    try {
      return `${goal.progress || 0}%`;
    } catch (err) {
      return '0%';
    }
  };

  return (
    <div className="goal-reminder-notification bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
      {error && (
        <div className="mb-2 p-2 bg-red-100 text-red-700 text-sm rounded">
          {error}
        </div>
      )}
      
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{getGoalName()}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">{getProgressText()} {getRemainingText() && `- ${getRemainingText()}`}</p>
          
          <div className="mt-2">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{ width: getProgressPercentage() }}
              ></div>
            </div>
          </div>
          
          <div className="mt-3 flex space-x-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleGoToGoal}
              isLoading={isLoading}
              disabled={isLoading}
            >
              View Goal
            </Button>
            {onDismiss && (
              <Button
                variant="outlined"
                size="sm"
                onClick={handleDismiss}
                isLoading={isLoading}
                disabled={isLoading}
              >
                Dismiss
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalReminderNotification;